import { Subscription } from 'rxjs';
import { FormControl, Validators } from '@angular/forms';
import { Component, Input, OnChanges, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { formatSystemDate } from 'src/app/shared/utils/system-date.util';
import { MatDialog as MatDialog } from '@angular/material/dialog';

import { ApiManualBatchService } from './../../../services/api/api-manual-batch.service';
import { ApiLotsService } from 'src/app/services/api/api-lots.service';
import { IInvoiceInfo, IRpaInfo } from './../../../models/annex';
import { ApiLotDetailService } from 'src/app/services/api/api-lot-detail.service';
import { SnackbarService } from './../../../services/snackbar.service';
import { ApiDialogService } from './../../../services/api/api-cdialog.service';
import { HeaderTitle, ILot } from 'src/app/models/generic';
import { DataService } from 'src/app/services/data.service';
import { DetailLotEnum, IContractLot } from '../../../models/generic';
import { DialogTypeEnum, IDialogConfirm } from 'src/app/models/dialog';
import { CampoNfComponent } from 'src/app/pages/detalhes-lotes/campo-nf/campo-nf.component';
import { StatusBadgeTone } from 'src/app/shared/components/status-badge/status-badge.component';
import { batchStatusTone } from 'src/app/shared/utils/status-badge.util';

export let LotTitle = '';
@Component({
  selector: 'app-detalhes-lotes-card',
  templateUrl: './detalhes-lotes-card.component.html',
  styleUrls: ['./detalhes-lotes-card.component.scss']
})
export class DetalhesLotesCardComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() lote!: ILot;
  @Input() typeDetail: number = DetailLotEnum.Transfer;
  @Input() isDialog: boolean = false;
  @Input() transferType: 'manual' | 'automatic' = 'automatic';
  @Input() appearance: 'legacy' | 'modern' = 'legacy';

  resumoExpanded = true;

  DetailLotEnum = DetailLotEnum;

  title!: HeaderTitle;
  lotCreated = '';
  infosDetailsArray!: {
    title: string;
    value: string | number | boolean | IContractLot[];
    action?: boolean;
    isHidden?: boolean;
    editField?: boolean;
  }[];

  paymentDate: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required]
  });

  Observacao: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required]
  });

  updateTemplateValuesSubscription!: Subscription;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _dataService: DataService,
    private _dialogService: ApiDialogService,
    private _snackbarService: SnackbarService,
    private _lotDetailService: ApiLotDetailService,
    private _manualBatchService: ApiManualBatchService,
    private _dialog: MatDialog,
    private _lotService: ApiLotsService
  ) {
    this.title = _activatedRoute.snapshot.routeConfig?.title as HeaderTitle;
  }

  ngOnInit(): void {
    if (this.typeDetail !== DetailLotEnum.Modal)
      this.updateHeaderData();

    this.listeningEvents();
  }

  ngOnChanges() {
    if (this.typeDetail !== DetailLotEnum.Modal) {
      this.updateHeaderData();
    }
    this.setLotValues();
  }

  private updateHeaderData(): void {
    const batchNumber = this.getBatchNumberForHeader();
    const title = `${this.title}${batchNumber ? ` ${batchNumber}` : ''}` as HeaderTitle;

    if (this.appearance === 'modern') {
      this.updateModernHeaderData(batchNumber);
      return;
    }

    this.lotCreated = title;

    this._dataService.setHeaderTitle(title);

    this._dataService.setHeaderInfos(
      this.lotCreated,
      this.lote?.NumberCurrentBatch,
      this.lote?.LegacyBatchNumber
    );
  }

  private updateModernHeaderData(batchNumber: string): void {
    this._dataService.setHeaderTitle('Faturamento dos Contratos');
    this._dataService.setHeaderInfos('', this.lote?.NumberCurrentBatch, this.lote?.LegacyBatchNumber);

    this._dataService.setSubheaderOverride({
      title: `Lote ${batchNumber}`.trim(),
      badgeLabel: this.modernStatusLabel,
      badgeTone: this.modernStatusTone,
      backLink: '/faturamento-contratos'
    });

    this._dataService.setHeaderSummary([
      { label: 'Contratos', value: this.lote?.NumberOfContracts ?? 0 },
      { label: 'Valor Faturado', value: this.currencyLabel(this.lote?.AmountTotal) },
      { label: 'Valor Financiado', value: this.currencyLabel(this.lote?.FinancingAmountTotal) }
    ]);
  }

  private currencyLabel(value?: number): string {
    return (value ?? 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
  }

  private getBatchNumberForHeader(): string {
    let firstPartnerBatchNumber: string | undefined;

    for (const partner of this.lote?.Partners ?? []) {
      const candidate = partner?.BatchNumber?.toString().trim();

      if (candidate) {
        firstPartnerBatchNumber = candidate;
        break;
      }
    }

    const candidates = [
      this.lote?.BatchNumber,
      this.lote?.NumberCurrentBatch,
      this.lote?.LegacyBatchNumber,
      firstPartnerBatchNumber
    ];

    return (
      candidates
        .map((value) => value?.toString().trim())
        .find((value) => !!value) ?? ''
    );
  }

  ngOnDestroy(): void {
    if (this.updateTemplateValuesSubscription)
      this.updateTemplateValuesSubscription.unsubscribe();
  }

  listeningEvents(): void {
    this._lotService.updateTemplateValuesEvent.subscribe({
      next: (invoice) => {
        this.updateTemplateValues(invoice);
      }
    });

    this._lotService.updateTemplateValuesEventRpa.subscribe({
      next: (rpa) => {
        this.updateTemplateValuesRpa(rpa);
      }
    });
  }

  setLotValues() {
    switch (this.typeDetail) {
      case DetailLotEnum.Invoicing:
        this.setInvoicingValues();
        break;

      case DetailLotEnum.Transfer:
        this.setTransferValues();
        break;
    }
  }

  setInvoicingValues(): void {
    let datePipe = new DatePipe('pt-BR');

    this.infosDetailsArray = [
      {
        title: 'Banco:',
        value: this.lote?.Bank
      },
      {
        title: 'Empresa:',
        value: this.lote?.Contracts?.[0].BeneficiaryName ?? '-'
      },
      {
        title: 'Nota Fiscal:',
        value: this.lote?.InvoiceNumber ?? '',
        isHidden: this.isDialog
      },
      {
        title: 'Dt. Prev. de Recebimento:',
        value:
          datePipe.transform(this.lote?.EstimatedPaymentDate, 'dd/MM/yyyy') ??
          '',
        isHidden: this.isDialog
      },
      {
        title: 'BPO/Regional:',
        value: this.lote?.BPO || '-',
        isHidden: this.isDialog
      },
      {
        title: 'Status do Lote:',
        value: this.lote?.BatchStatus || '-'
      },
      {
        title: 'Valor Total de Financiamento:',
        value: this.isDialog
          ? this.amountValueInvoicing(
            this.lote?.Contracts ?? []
          ).toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL'
          })
          : this.lote?.FinancingAmountTotal?.toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL'
          }) || 0
      },
      {
        title: 'Valor Faturado:',
        value: this.lote?.AmountTotal.toLocaleString('pt-br', {
          style: 'currency',
          currency: 'BRL'
        })
      },
      {
        title: 'Data de Emissão da NF:',
        value: datePipe.transform(this.lote?.InvoiceDate, 'dd/MM/yyyy') ?? '',
        isHidden: this.isDialog
      },
      {
        title: 'Qtde de Contratos:',
        value: this.lote?.NumberOfContracts
      },
      {
        title: 'Vl. Tot. Comissão:',
        value: this.lote?.AmountTotal.toLocaleString('pt-br', {
          style: 'currency',
          currency: 'BRL'
        })
      },
      {
        title: 'Tipo de Operação:',
        value: this.lote?.OperationType || '-'
      },
      {
        title: 'Data Cancelamento:',
        value:
          datePipe.transform(this.lote?.CancelInvoideDate, 'dd/MM/yyyy') ?? '',
        isHidden: this.isDialog
      },
      {
        title: 'Status do Processamento:',
        value: this.setInvoiceStatusProcessing()
      },
      {
        title: 'Data do Lote:',
        value: new Date(this.lote?.CreateDate).toLocaleDateString('pt-br')
      },
      {
        title: 'Data de Envio do E-mail:',
        value:
          formatSystemDate(this.lote?.EmailInvoiceDate, 'dd/MM/yyyy') ?? '',
        isHidden: this.isDialog
      },
      {
        title: 'Data de Recebimento:',
        value: datePipe.transform(this.lote?.PaymentDate, 'dd/MM/yyyy') ?? '',
        isHidden: this.isDialog,
        editField:
          !this.lote?.PaymentDate &&
          this.lote?.BatchStatus?.toLowerCase() === 'faturado'
      },
      {
        title: 'Observação:',
        value: this.lote?.Observacao ?? '',
        editField: true
      }
    ];

    this.Observacao.patchValue(this.lote?.Observacao?.toString() ?? '');

  }

  setTransferValues(): void {
    let datePipe = new DatePipe('pt-BR');

    this.infosDetailsArray = [
      {
        title: 'Status do Lote:',
        value: this.lote?.StatusOfOnlending || '-'
      },
      {
        title: 'Parceiro:',
        value: this.lote?.Partner,
        isHidden: this.transferType === 'manual'
      },
      {
        title: 'N° Pedido OMIE :',
        value: this.lote?.SapCode!,
        isHidden: this.isDialog || this.transferType === 'manual'
      },
      {
        title: 'Data do Lote:',
        value: new Date(this.lote?.CreateDate).toLocaleDateString('pt-br')
      },
      {
        title: 'Data e-mail pgto:',
        value: datePipe.transform(this.lote?.EmailEstimatedPaymentDate, 'dd/MM/yyyy') ?? ''
      },
      {
        title: 'Data Prevista de Pagamento:',
        value:
          datePipe.transform(this.lote?.EstimatedPaymentDate, 'dd/MM/yyyy') ??
          '',
        isHidden: this.isDialog || this.transferType === 'manual'
      },
      {
        title: 'Nota Fiscal:',
        value: this.lote?.InvoiceNumber ?? '',
        action: this.typeDetail === DetailLotEnum.Transfer,
        isHidden: this.isDialog || this.transferType === 'manual'
      },
      {
        title: 'Qtde de Contratos:',
        value: this.lote?.NumberOfContracts ?? 0
      },
      {
        title: 'Valor Total de Financiamento:',
        value: this.amountValueAutomaticTransfer(
          this.lote?.Contracts ?? []
        ).toLocaleString('pt-br', {
          style: 'currency',
          currency: 'BRL'
        }),
        isHidden: this.transferType === 'manual'
      },
      {
        title: 'Valor Total de Financiamento:',
        value: this.amountValueManualTransfer(
          this.lote?.Partners ?? []
        ).toLocaleString('pt-br', {
          style: 'currency',
          currency: 'BRL'
        }),
        isHidden: this.transferType === 'automatic'
      },
      {
        title: 'Data de Emissão da NF:',
        value: datePipe.transform(this.lote?.InvoiceDate, 'dd/MM/yyyy') ?? '',
        action: this.typeDetail === DetailLotEnum.Transfer,
        isHidden: this.isDialog || this.transferType === 'manual'
      },
      {
        title: 'Banco:',
        value: this.lote?.Bank,
        isHidden: this.transferType === 'manual'
      },
      {
        title: 'Valor do Repasse:',
        value: this.lote?.AmountTotal.toLocaleString('pt-br', {
          style: 'currency',
          currency: 'BRL'
        })
      },
      {
        title: 'Data do Pedido de Pagamento:',
        value: datePipe.transform(this.lote?.PaymentDate, 'dd/MM/yyyy') ?? '',
        isHidden: this.isDialog || this.transferType === 'manual'
      },
      {
        title: 'Data Prev. de Pagamento:',
        value:
          datePipe.transform(this.lote?.EstimatedPaymentDate, 'dd/MM/yyyy') ??
          '',
        isHidden: this.isDialog || this.transferType === 'manual',
        editField: !this.lote?.Integrated
      },
      {
        title: 'Data de Integração OMIE (Lote):',
        value:
          datePipe.transform(this.lote?.IntegrationDate, 'dd/MM/yyyy') ?? '',
        isHidden: this.isDialog || this.transferType === 'manual'
      },
      {
        title: 'Valor Total de Estorno:',
        value:
          this.lote?.TotalReversed!.toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL'
          }) || '-',
        isHidden: !this.lote?.TotalReversed
      },
      {
        title: 'Observação:',
        value: this.lote?.Observacao ?? '',
        editField: true
      }
    ];

    this.Observacao.patchValue(this.lote?.Comment?.toString() ?? '');
    this.paymentDate.setValue(this.paymentDateLocationEn ?? '');
  }

  get numberOfContracts(): number {
    let amount: number = 0;

    this.lote?.Partners?.forEach((partner) => {
      amount += partner.Contracts?.length ?? 0;
    });

    return amount;
  }

  get paymentDateLocationEn(): string | null {
    let datePipe = new DatePipe('en-US');
    let dateTransform = datePipe.transform(
      this.lote?.EstimatedPaymentDate ?? '',
      'yyyy-MM-dd'
    );
    return dateTransform;
  }

  handlePaymentDate(): void {
    switch (this.typeDetail) {
      case DetailLotEnum.Invoicing:
        this.openDialogConfirmation();
        break;

      case DetailLotEnum.Transfer:
        this.saveTransferEstimatedPaymentDate();
        break;
    }
  }

  handleObservacao(): void {
    this.openObservacaoConfirmation();
  }

  openDialogConfirmation(): void {
    const question = `Tem certeza que deseja cadastrar a data (${this.paymentDateLocation})?`;

    const dialogData: IDialogConfirm = {
      type: DialogTypeEnum.Optional,
      appearance: 'modern',
      template: {
        boldTitle: 'Confirmar data de recebimento',
        title: question,
        description: 'Não será possível editar novamente após a confirmação.',
        confirm: 'Sim',
        dismiss: 'Não'
      }
    };

    this._dialogService.openDialog(dialogData).then((response) => {
      if (response) {
        this.saveInvoicePaymentDate();
      }
    });
  }

  openObservacaoConfirmation(): void {
    const dialogData: IDialogConfirm = {
      type: DialogTypeEnum.Optional,
      appearance: 'modern',
      template: {
        boldTitle: 'Confirmar observação',
        title: 'Tem certeza que deseja atualizar a observação?',
        confirm: 'Sim',
        dismiss: 'Não'
      }
    };

    this._dialogService.openDialog(dialogData).then((response) => {
      if (response) {

        switch (this.typeDetail) {
          case DetailLotEnum.Invoicing:
            this.saveInvoiceComment();
            break;

          case DetailLotEnum.Transfer:
            this.saveTransferComment();
            break;
        }
      }
    });
  }

  get paymentDateLocation(): string | null {
    let datePipe = new DatePipe('pt-BR');
    let dateTransform = datePipe.transform(
      this.paymentDate.value,
      'dd/MM/yyyy'
    );
    return dateTransform;
  }

  saveInvoicePaymentDate(): void {
    const paymentDate: { PaymentDate: string } = {
      PaymentDate: this.paymentDate.value
    };

    this._lotDetailService
      .createPaymentDate(this.lote.BatchId.toString(), paymentDate)
      .subscribe({
        next: (response) => {
          this.lote.PaymentDate = this.paymentDate.value;
          this.setInvoicingValues();
          this._snackbarService.openSnackBar(
            'Data cadastrada com sucesso!',
            'success',
            3000
          );
        }
      });
  }

  saveInvoiceComment(): void {
    const Comment: { Comment: string } = {
      Comment: this.Observacao.value
    };

    this._lotDetailService
      .updateCommentInvoice(this.lote.BatchId.toString(), Comment)
      .subscribe({
        next: (response) => {
          this.lote.Observacao = this.Observacao.value;
          this.setInvoicingValues();
          this._snackbarService.openSnackBar(
            'Observação cadastrada com sucesso!',
            'success',
            3000
          );
        }
      });
  }

  saveTransferComment(): void {
    const Comment: { Comment: string } = {
      Comment: this.Observacao.value
    };

    this._lotDetailService
      .updateCommentTransfer(this.lote.BatchId.toString(), Comment)
      .subscribe({
        next: (response) => {
          this.lote.Comment = this.Observacao.value;
          this.setTransferValues();
          this._snackbarService.openSnackBar('Observação cadastrada com sucesso!', 'success', 3000);
        }
      });
  }

  saveTransferEstimatedPaymentDate(): void {
    const paymentDate: { EstimatedPaymentDate: string } = {
      EstimatedPaymentDate: this.paymentDate.value
    };

    this._manualBatchService
      .saveEstimatedPaymentDate(this.lote.BatchId.toString(), paymentDate)
      .subscribe({
        next: (response) => {
          this.lote.EstimatedPaymentDate = this.paymentDate.value;
          this._snackbarService.openSnackBar(
            'Data cadastrada com sucesso!',
            'success',
            3000
          );
        }
      });
  }

  handleInvoiceInfo(index: number): void {
    const data: IInvoiceInfo = {
      BatchId: this.lote.BatchId.toString(),
      InvoiceDate: this.lote.InvoiceDate ?? '',
      InvoiceNumber: this.lote.InvoiceNumber ?? ''
    };
    const dialogRef = this._dialog.open(CampoNfComponent, {
      disableClose: true,
      data
    });
    dialogRef.afterClosed().subscribe((result: IInvoiceInfo | boolean) => {
      if (result) {
        this.updateInvoiceInfo(result as IInvoiceInfo);
      }
    });
  }

  updateInvoiceInfo(invoice: IInvoiceInfo): void {
    this._lotService.postInvoiceInfo(invoice).subscribe({
      next: (response) => {
        this._snackbarService.openSnackBar(
          'Informações alteradas com sucesso!',
          'success'
        );
        this.updateTemplateValues(invoice);
      }
    });
  }

  updateTemplateValues(invoice: IInvoiceInfo): void {
    this.lote.InvoiceDate = invoice.InvoiceDate;
    this.lote.InvoiceNumber = invoice.InvoiceNumber;
    this.lote.IsInvoiced = true;
    this.lote.StatusOfOnlending = 'Aprovado';
    this.setLotValues();
  }

  updateTemplateValuesRpa(rpa: IRpaInfo): void {
    this.lote.RpaDate = rpa.RpaDate;
    this.lote.RpaNumber = rpa.RpaNumber;
    this.lote.HasRpa = true;
    this.lote.StatusOfOnlending = 'Aprovado';
    this.setLotValues();
  }

  amountValueAutomaticTransfer(contracts: IContractLot[]): number {
    let amountTotal: number = 0;

    amountTotal =
      contracts.reduce((accum, curr) => {
        return accum + curr.Amount;
      }, 0) ?? 0;

    return amountTotal;
  }

  amountValueManualTransfer(partners: ILot[]): number {
    let amountTotal = 0;

    partners.forEach((partner) => {
      amountTotal += this.amountValueAutomaticTransfer(
        partner?.Contracts?.filter(x => !x.IsSubestabelecidoContract) ?? []
      );

      amountTotal -= this.amountValueAutomaticTransfer(partner?.Reverses?.filter(x => !x.IsSubestabelecidoContract) ?? []);
    });

    return amountTotal;
  }

  amountValueInvoicing(contracts: IContractLot[]): number {
    let amountTotal = 0;

    contracts.forEach((contract) => {
      if (
        contract.Status.toLowerCase() === 'a estornar' ||
        contract.Status.toLowerCase() === 'estornar' ||
        contract.Status.toLowerCase() === 'estornado parcial'
      ) {
        amountTotal -= contract?.Amount ?? 0;
      } else {
        amountTotal += contract?.Amount ?? 0;
      }
    });

    return amountTotal;
  }

  setInvoiceStatusProcessing(): string {
    if (this.lote?.InvoiceNumber && !this.lote?.SapIntegrationError) {
      return 'Processado';
    } else if (!this.lote?.InvoiceNumber && !this.lote?.SapIntegrationError) {
      return 'Aguardando';
    } else {
      return 'Não processado';
    }
  }

  get modernStatusLabel(): string {
    return (this.lote?.BatchStatus || this.lote?.StatusOfOnlending || '-') as string;
  }

  get modernStatusTone(): StatusBadgeTone {
    return batchStatusTone(this.lote?.BatchStatus || this.lote?.StatusOfOnlending);
  }

  toggleResumo(): void {
    this.resumoExpanded = !this.resumoExpanded;
  }

  setTransferStatusProcessing(): string {
    if (this.lote?.OrderNumber && this.lote?.PaymentDate) {
      return 'Pago';
    } else if (this.lote?.OrderNumber && this.lote?.Integrated) {
      return 'Processado com sucesso';
    } else if (this.lote?.OrderNumber) {
      return 'Em andamento';
    }

    return 'Aguardando';
  }
}
